import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { AgentUiBlocks } from './AgentUiBlocks';
import { AgentRequestsPanel, fmtH } from './AgentRequestsPanel';

const PRIO_COLORS = { P0: 'bg-rose-600', P1: 'bg-rose-500', P2: 'bg-orange-500', P3: 'bg-amber-500', P4: 'bg-gray-400' };
const EMOJIS = ['🤖', '🧑‍🍳', '🦊', '🌟', '🍪', '⚡'];

export const AgentBubble = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('chat');
  const [prefs, setPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('boufi_agent_prefs')) || {}; } catch { return {}; }
  });
  const [editPrefs, setEditPrefs] = useState(false);
  const [prio, setPrio] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [waNumber, setWaNumber] = useState('');
  const [waLoaded, setWaLoaded] = useState(false);
  const [reqs, setReqs] = useState(null);
  const [reqExec, setReqExec] = useState(null);
  const endRef = useRef(null);
  const agentName = prefs.nom || 'Mon Agent';
  const emoji = prefs.emoji || '🤖';

  const refreshReqs = () => api.get('/agent-os/requests?mark_seen=1').then((r) => setReqs(r.data)).catch(() => {});

  useEffect(() => {
    if (!user) return;
    api.get('/priorities/me').then((r) => setPrio(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const tick = () => api.get(open ? '/agent-os/requests?mark_seen=1' : '/agent-os/requests')
      .then((r) => setReqs(r.data)).catch(() => {});
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, [user, open]);

  useEffect(() => {
    const h = () => { setOpen(true); setTab('demandes'); };
    const h2 = () => { setOpen(true); setTab('chat'); };
    window.addEventListener('boufi:open-demandes', h);
    window.addEventListener('boufi:open-agent', h2);
    return () => { window.removeEventListener('boufi:open-demandes', h); window.removeEventListener('boufi:open-agent', h2); };
  }, []);

  useEffect(() => {
    if (!open) return;
    api.get('/lia/chat/history').then((r) => {
      const hist = r.data.messages || [];
      const today = new Date().toISOString().slice(0, 10);
      if (localStorage.getItem('boufi_brief_seen') !== today) {
        api.get('/agent-os/brief').then((b) => {
          setMsgs([...hist, { role: 'assistant', content: b.data.contenu }]);
          localStorage.setItem('boufi_brief_seen', today);
        }).catch(() => setMsgs(hist));
      } else setMsgs(hist);
    }).catch(() => {});
    api.get('/priorities/me').then((r) => setPrio(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!editPrefs || waLoaded) return;
    api.get('/agent-os/me/whatsapp').then((r) => { setWaNumber(r.data.whatsapp_number || ''); setWaLoaded(true); }).catch(() => {});
  }, [editPrefs, waLoaded]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, busy, tab]);

  if (!user) return null;

  const savePrefs = (p) => {
    setPrefs(p);
    localStorage.setItem('boufi_agent_prefs', JSON.stringify(p));
  };

  const send = async (textArg) => {
    const text = (typeof textArg === 'string' ? textArg : input).trim();
    if (!text || busy) return;
    setInput('');
    setMsgs((m) => [...m, { role: 'user', content: text, created_at: new Date().toISOString() }]);
    setBusy(true);
    try {
      const r = await api.post('/lia/chat', { message: text });
      setMsgs((m) => [...m, { role: 'assistant', content: r.data.reply, tools: r.data.tools_used, ui: r.data.ui, created_at: new Date().toISOString() }]);
      if (r.data.pending_action?.type) setPending(r.data.pending_action);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur agent');
      setMsgs((m) => [...m, { role: 'assistant', content: '⚠️ Je n\'ai pas pu répondre, réessaie.' }]);
    } finally { setBusy(false); }
  };

  const urgents = prio?.urgents || 0;
  const reqBadge = (reqs?.en_attente || 0) + (reqs?.reponses_non_vues || 0);

  return (
    <>
      {!open && location.pathname !== '/operations' && (
        <button data-testid="agent-bubble-btn" onClick={() => setOpen(true)} aria-label="Ouvrir mon agent"
          className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-rose-500 shadow-xl shadow-orange-900/30 flex items-center justify-center text-2xl hover:scale-105 transition-transform">
          {emoji}
          {urgents > 0 && (
            <span data-testid="agent-bubble-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-rose-600 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white">{urgents}</span>
          )}
          {reqBadge > 0 && (
            <span data-testid="agent-bubble-req-badge" className="absolute -bottom-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-sky-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white">{reqBadge}</span>
          )}
        </button>
      )}

      {open && (
        <div data-testid="agent-bubble-panel"
          className="fixed inset-0 md:inset-auto md:bottom-6 md:right-6 z-50 md:w-[400px] md:max-h-[75vh] md:rounded-3xl bg-gray-950 text-white shadow-2xl border border-white/10 flex flex-col overflow-hidden">
          <div className="flex items-center gap-3 p-4 border-b border-white/10 bg-gradient-to-r from-orange-500/15 to-transparent">
            <span className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center text-xl flex-shrink-0">{emoji}</span>
            <div className="flex-1 min-w-0">
              <div className="font-extrabold text-[15px] truncate">{agentName}</div>
              <div className="text-[11px] text-white/50 truncate">Agent de {user.name?.split(' ')[0]} · connaît ton rôle et ton périmètre</div>
            </div>
            <button data-testid="agent-attention-btn" title="Mon Attention"
              onClick={() => { setOpen(false); window.location.assign('/attention'); }}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20">✨</button>
            <button data-testid="agent-prefs-btn" onClick={() => setEditPrefs(!editPrefs)} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20">⚙️</button>
            <button data-testid="agent-bubble-close" onClick={() => setOpen(false)} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20">✕</button>
          </div>

          <div className="flex border-b border-white/10" data-testid="agent-tabs">
            <button data-testid="agent-tab-chat" onClick={() => setTab('chat')}
              className={`flex-1 min-h-[44px] text-[13px] font-bold ${tab === 'chat' ? 'text-orange-300 border-b-2 border-orange-400 bg-white/[0.04]' : 'text-white/50 hover:text-white/80'}`}>
              💬 Chat
            </button>
            <button data-testid="agent-tab-demandes" onClick={() => setTab('demandes')}
              className={`flex-1 min-h-[44px] text-[13px] font-bold relative ${tab === 'demandes' ? 'text-orange-300 border-b-2 border-orange-400 bg-white/[0.04]' : 'text-white/50 hover:text-white/80'}`}>
              📨 Demandes
              {reqBadge > 0 && (
                <span className="absolute top-1.5 ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-sky-500 text-white text-[10px] font-bold inline-flex items-center justify-center">{reqBadge}</span>
              )}
            </button>
          </div>

          {editPrefs && (
            <div className="p-3 border-b border-white/10 bg-white/5 space-y-2" data-testid="agent-prefs-form">
              <div className="flex items-center gap-2 flex-wrap">
                <input value={prefs.nom || ''} placeholder="Nom de ton agent" data-testid="agent-prefs-nom"
                  onChange={(e) => savePrefs({ ...prefs, nom: e.target.value })}
                  className="flex-1 min-w-[140px] bg-white/10 border border-white/15 rounded-xl px-3 py-2 text-sm placeholder:text-white/30" />
                {EMOJIS.map((em) => (
                  <button key={em} onClick={() => savePrefs({ ...prefs, emoji: em })}
                    className={`w-9 h-9 rounded-lg text-lg ${emoji === em ? 'bg-orange-500/30 ring-1 ring-orange-400' : 'bg-white/5 hover:bg-white/15'}`}>{em}</button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input value={waNumber} placeholder="WhatsApp (+33612345678) — brief 7h30" data-testid="agent-prefs-whatsapp"
                  onChange={(e) => setWaNumber(e.target.value)}
                  className="flex-1 bg-white/10 border border-white/15 rounded-xl px-3 py-2 text-sm placeholder:text-white/30" />
                <button data-testid="agent-prefs-whatsapp-save"
                  onClick={async () => {
                    try {
                      await api.put('/agent-os/me/whatsapp', { whatsapp_number: waNumber });
                      toast.success(waNumber ? 'Numéro enregistré — brief quotidien à 7h30 📲' : 'Numéro supprimé');
                    } catch (e) { toast.error(e.response?.data?.detail || 'Numéro invalide'); }
                  }}
                  className="px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold">OK</button>
              </div>
            </div>
          )}

          {tab === 'demandes' ? (
            <AgentRequestsPanel reqs={reqs} refresh={refreshReqs} userEmail={user.email}
              onExec={(r) => {
                setReqExec({ id: r.id, de_nom: r.de_nom });
                setTab('chat');
                send(`⚡EXÉCUTION DEMANDE de ${r.de_nom} : « ${r.objet} »`);
              }} />
          ) : (<>
          {prio && prio.items?.length > 0 && (
            <div className="p-3 border-b border-white/10 bg-white/[0.03]" data-testid="agent-priorities">
              <div className="text-[11px] uppercase tracking-wide text-white/40 font-bold mb-1.5">🔥 Tes priorités ({prio.total})</div>
              {prio.items.slice(0, 3).map((p) => (
                <div key={p.ref} className="flex items-center gap-2 py-1" data-testid={`agent-prio-${p.ref}`}>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${PRIO_COLORS[p.niveau]} text-white`}>{p.niveau}</span>
                  <span className="text-xs text-white/80 truncate flex-1">{p.titre}</span>
                  <button onClick={() => { api.post('/priorities/dismiss', { ref: p.ref }).then(() => api.get('/priorities/me').then((r) => setPrio(r.data))); }}
                    className="text-white/30 hover:text-white text-xs px-1" title="Ignorer" data-testid={`agent-prio-dismiss-${p.ref}`}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]" data-testid="agent-messages">
            {reqs?.en_attente > 0 && (
              <button data-testid="agent-demandes-banner" onClick={() => setTab('demandes')}
                className="w-full rounded-2xl border border-sky-400/40 bg-sky-500/15 px-3.5 py-3 text-left text-sm font-bold text-sky-200 hover:bg-sky-500/25">
                📨 {reqs.en_attente} demande{reqs.en_attente > 1 ? 's' : ''} à traiter — appuie ici pour répondre →
              </button>
            )}
            {msgs.length === 0 && (
              <div className="text-sm text-white/50">Bonjour {user.name?.split(' ')[0]} 👋 Pose-moi une question sur ton activité — je connais ton rôle et ton périmètre.</div>
            )}
            {msgs.map((m, i) => (
              <div key={i}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === 'user' ? 'ml-auto bg-orange-500/90 text-white rounded-br-md' : 'bg-white/10 text-white/90 rounded-bl-md'}`}>
                  {m.content}
                  {m.tools?.length > 0 && <div className="text-[10px] text-white/40 mt-1">🔧 {m.tools.map((t) => (typeof t === 'string' ? t : t?.name || t?.tool || 'outil')).join(', ')}</div>}
                  {m.created_at && <div className={`text-[9px] mt-1 text-right ${m.role === 'user' ? 'text-white/70' : 'text-white/35'}`}>{fmtH(m.created_at)}</div>}
                </div>
                {m.role !== 'user' && m.ui?.length > 0 && (
                  <div className="mt-2"><AgentUiBlocks blocks={m.ui} onAction={(msg) => send(msg)} /></div>
                )}
              </div>
            ))}
            {busy && <div className="bg-white/10 rounded-2xl px-3.5 py-2.5 text-sm w-16 text-white/60 animate-pulse">…</div>}
            {pending && (
              <div className="rounded-2xl border border-orange-400/40 bg-orange-500/10 p-3.5" data-testid="agent-pending-action">
                <div className="text-[11px] uppercase tracking-wide text-orange-300 font-bold mb-1">Confirmation requise</div>
                <div className="text-sm text-white/90">{pending.resume}</div>
                <div className="flex gap-2 mt-3">
                  <button data-testid="agent-action-confirm" disabled={busy}
                    className="flex-1 text-[13px] font-bold min-h-[42px] rounded-full bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
                    onClick={async () => {
                      setBusy(true);
                      try {
                        const r = await api.post('/lia/execute-action', { type: pending.type, params: pending.params });
                        setMsgs((m) => [...m, { role: 'assistant', content: r.data.message, created_at: new Date().toISOString() }]);
                        setPending(null);
                        if (reqExec) {
                          try {
                            await api.post(`/agent-os/requests/${reqExec.id}/reply`, { reponse: `✅ Exécuté par mon agent : ${r.data.message}` });
                            toast.success(`Réponse automatique envoyée à ${reqExec.de_nom} 📨`);
                            refreshReqs();
                          } catch { /* déjà répondue */ }
                          setReqExec(null);
                        }
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Erreur exécution');
                        setMsgs((m) => [...m, { role: 'assistant', content: `⛔ ${e.response?.data?.detail || 'Action refusée.'}` }]);
                        setPending(null);
                      } finally { setBusy(false); }
                    }}>✅ Confirmer</button>
                  <button data-testid="agent-action-cancel"
                    className="text-[13px] font-bold px-4 min-h-[42px] rounded-full border border-white/20 text-white/70 hover:text-white"
                    onClick={() => { setPending(null); setMsgs((m) => [...m, { role: 'assistant', content: 'Action annulée. 👍' }]); }}>Annuler</button>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-white/10 flex gap-2">
            <input data-testid="agent-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Écris à ton agent…"
              className="flex-1 bg-white/10 border border-white/15 rounded-full px-4 min-h-[46px] text-sm placeholder:text-white/30 focus:outline-none focus:border-orange-400" />
            <button data-testid="agent-send" onClick={send} disabled={busy}
              className="w-[46px] h-[46px] rounded-full bg-gradient-to-br from-orange-500 to-rose-500 text-white font-bold disabled:opacity-50">➤</button>
          </div>
          </>)}
        </div>
      )}
    </>
  );
};
